import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, forkJoin } from 'rxjs';
import { catchError, map, tap, switchMap } from 'rxjs/operators';
import {
  Document,
  DocumentStatus,
  DocumentType,
  Employee,
  NotificationChannel,
  NotificationRecord,
  NotificationType,
  PersonalEvent,
  PersonalEventType
} from '../../models/hr-models';
import { HrService } from './hr.service';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private apiUrl = 'api/notifications';

  constructor(
    private http: HttpClient,
    private hrService: HrService
  ) {}

  // Check for notifications that need to be sent
  checkForNotifications(): Observable<boolean> {
    return forkJoin([
      this.checkDocumentExpirations(),
      this.checkPersonalEvents()
    ]).pipe(
      map(results => results.some(result => result)),
      catchError(error => {
        console.error('Error checking for notifications:', error);
        return of(false);
      })
    );
  }

  // Check document expirations
  private checkDocumentExpirations(): Observable<boolean> {
    return this.hrService.notificationSettings$.pipe(
      map(settings => {
        // Set default array if documentExpiryReminderDays is undefined
        const reminderDays = settings.documentExpiryReminderDays || [30, 15, 7, 1];
        let notificationsSent = false;

        reminderDays.forEach(days => {
          this.hrService.getExpiringDocuments(days).subscribe(expiringDocs => {
            expiringDocs.forEach(doc => {
              // Check if we've already sent a notification for this day
              const alreadySentForThisInterval = doc.notificationsSent?.some(
                notification => {
                  const today = new Date();
                  const notificationDate = new Date(notification.sentDate);
                  return (
                    notification.notificationType === NotificationType.DocumentExpiring &&
                    notificationDate.toDateString() === today.toDateString()
                  );
                }
              );

              if (!alreadySentForThisInterval) {
                this.sendDocumentExpirationNotification(doc, days);
                notificationsSent = true;
              }
            });
          });
        });

        return notificationsSent;
      }),
      catchError(error => {
        console.error('Error checking document expirations:', error);
        return of(false);
      })
    );
  }

  // Check for birthdays and anniversaries
  private checkPersonalEvents(): Observable<boolean> {
    return forkJoin([
      this.hrService.notificationSettings$,
      this.hrService.getEmployees()
    ]).pipe(
      map(([settings, employees]) => {
        const today = new Date();
        let notificationsSent = false;

        // Check birthdays with fallback default
        const birthdayReminderDays = settings.birthdayReminderDays || 3;
        employees.forEach(employee => {
          const birthDate = new Date(employee.birthDate);
          if (this.isDateApproaching(birthDate, today, birthdayReminderDays)) {
            this.sendBirthdayNotification(employee);
            notificationsSent = true;
          }
        });

        // Check work anniversaries with fallback default
        const anniversaryReminderDays = settings.anniversaryReminderDays || 5;
        employees.forEach(employee => {
          const hireDate = new Date(employee.hireDate);
          if (this.isDateApproaching(hireDate, today, anniversaryReminderDays)) {
            this.sendAnniversaryNotification(employee);
            notificationsSent = true;
          }
        });

        return notificationsSent;
      }),
      catchError(error => {
        console.error('Error checking personal events:', error);
        return of(false);
      })
    );
  }

  // Helper method to check if a date is approaching
  private isDateApproaching(date: Date, today: Date, daysAhead: number): boolean {
    const targetMonth = date.getMonth();
    const targetDay = date.getDate();

    // Calculate the target date for this year
    const targetDate = new Date(today.getFullYear(), targetMonth, targetDay);

    // If the date has passed this year, check for next year
    if (targetDate < today) {
      targetDate.setFullYear(today.getFullYear() + 1);
    }

    // Calculate days difference
    const diffTime = targetDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays >= 0 && diffDays <= daysAhead;
  }

  // Send document expiration notification
  sendDocumentExpirationNotification(document: Document, daysRemaining: number): Observable<NotificationRecord | null> {
    return this.hrService.getEmployeeById(document.employeeId).pipe(
      switchMap(employee => {
        if (!employee) {
          console.error(`Employee with ID ${document.employeeId} not found`);
          return of(null);
        }

        const notification: NotificationRecord = {
          id: 0, // Will be set by the server
          documentId: document.id,
          employeeId: employee.id,
          notificationType: NotificationType.DocumentExpiring,
          sentDate: new Date(),
          channel: NotificationChannel.Both, // Default to both channels
          success: false // Will be updated after sending
        };

        return this.sendNotification(notification, {
          recipientEmail: employee.email,
          recipientName: employee.name,
          subject: `Document Expiring in ${daysRemaining} days: ${document.title}`,
          message: `Your ${document.documentType} document "${document.title}" will expire in ${daysRemaining} days. Please take action to renew it.`
        });
      }),
      catchError(error => {
        console.error('Error sending document expiration notification:', error);
        return of(null);
      })
    );
  }

  // Send birthday notification
  sendBirthdayNotification(employee: Employee): Observable<NotificationRecord | null> {
    const notification: NotificationRecord = {
      id: 0, // Will be set by the server
      documentId: 0, // Not applicable for birthday
      employeeId: employee.id,
      notificationType: NotificationType.Birthday,
      sentDate: new Date(),
      channel: NotificationChannel.Both,
      success: false
    };

    return this.sendNotification(notification, {
      recipientEmail: employee.email,
      recipientName: employee.name,
      subject: 'Birthday Celebration',
      message: `Happy Birthday ${employee.name}! We hope you have a wonderful day.`
    });
  }

  // Send work anniversary notification
  sendAnniversaryNotification(employee: Employee): Observable<NotificationRecord | null> {
    const hireDate = new Date(employee.hireDate);
    const today = new Date();
    const yearsOfService = today.getFullYear() - hireDate.getFullYear();

    const notification: NotificationRecord = {
      id: 0, // Will be set by the server
      documentId: 0, // Not applicable for anniversary
      employeeId: employee.id,
      notificationType: NotificationType.WorkAnniversary,
      sentDate: new Date(),
      channel: NotificationChannel.Both,
      success: false
    };

    return this.sendNotification(notification, {
      recipientEmail: employee.email,
      recipientName: employee.name,
      subject: `Work Anniversary: ${yearsOfService} Years`,
      message: `Congratulations ${employee.name} on your ${yearsOfService} year work anniversary! Thank you for your dedication and contributions.`
    });
  }

  // Send the actual notification
  private sendNotification(
    notificationRecord: NotificationRecord,
    details: {
      recipientEmail: string,
      recipientName: string,
      subject: string,
      message: string
    }
  ): Observable<NotificationRecord | null> {
    // First record the notification in our system
    return this.hrService.sendNotification(notificationRecord).pipe(
      tap(savedNotification => {
        if (!savedNotification) return;

        // Then send the actual email/push notification
        const payload = {
          ...details,
          notificationId: savedNotification.id,
          notificationType: savedNotification.notificationType,
          channel: savedNotification.channel
        };

        // Send to the notifications API
        this.http.post(`${this.apiUrl}/send`, payload).subscribe(
          () => {
            console.log(`Notification sent successfully to ${details.recipientName}`);
            // Update the notification record with success status
            const updatedNotification = { ...savedNotification, success: true };
            this.hrService.updateDocument(updatedNotification as any); // Type assertion as a workaround
          },
          error => {
            console.error('Error sending notification:', error);
          }
        );
      }),
      catchError(error => {
        console.error('Error recording notification:', error);
        return of(null);
      })
    );
  }
}
